import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  ApiResponse,
  PatronModel,
  SaleEvent,
  TransactionModel,
  TransactionWrapper,
} from '@core/models';
import {
  ApiException,
  BaseConnectionService,
} from '@core/services/base-connection.service';
import { Client } from '@shared/models/client.model';
import { catchError, map, Observable, Subject, tap, throwError } from 'rxjs';
import { PaymentDetails } from '../models/payment-details.model';

@Injectable({
  providedIn: 'root',
})
export class ConnectionService extends BaseConnectionService {
  private http = inject(HttpClient);
  eventSubject: Subject<SaleEvent> = new Subject<SaleEvent>();
  token: string;

  getTransactionById(transactionId: string, brainTree = true) {
    const endpoint = `${this.apiURL}/transaction/${transactionId}?token=${this.token}&braintree=${brainTree}`;
    return this.sendRequest<TransactionWrapper>(this.http, endpoint).pipe(
      tap(data =>
        this.eventSubject.next(data.data?.transaction?.event as SaleEvent)
      )
    );
  }

  editTransactionBuyerType(
    transactionId: string,
    buyerTypeId: number,
    buyerTypeName: string
  ) {
    const endpoint = `${this.apiURL}/transaction/${transactionId}?token=${this.token}`;
    const body = { buyerType: { id: buyerTypeId, name: buyerTypeName } };
    const observable = this.http.patch<ApiResponse<TransactionModel>>(
      endpoint,
      body,
      this.httpOptions
    );
    return this.handleApiResponse(observable);
  }

  deleteTransaction(transactionId: string) {
    const endpoint = `${this.apiURL}/transaction/${transactionId}?token=${this.token}`;
    const observable = this.http.delete<
      ApiResponse<Pick<TransactionModel, 'id'>>
    >(endpoint, this.httpOptions);
    return this.handleApiResponse(observable);
  }

  addRainoutInsuranceToTransaction(transactionId: string) {
    const endpoint = `${this.apiURL}/transaction/${transactionId}/add/rainout-insurance?token=${this.token}`;
    return this.sendRequest<TransactionModel>(this.http, endpoint, {});
  }

  deleteRainoutInsuranceToTransaction(transactionId: string) {
    const endpoint = `${this.apiURL}/transaction/${transactionId}/delete/rainout-insurance?token=${this.token}`;
    const observable = this.http.delete<ApiResponse<TransactionModel>>(
      endpoint,
      {}
    );
    return this.handleApiResponse(observable);
  }

  applyDiscountCode(transactionId: string, code: string) {
    const endpoint = `${this.apiURL}/transaction/${transactionId}/discount-code?token=${this.token}`;
    const body = { code };
    const observable = this.http.post<ApiResponse<TransactionModel>>(
      endpoint,
      body,
      this.httpOptions
    );
    return this.handleApiResponse(observable);
  }

  removeDiscountCode(transactionId: string) {
    const endpoint = `${this.apiURL}/transaction/${transactionId}/discount-code?token=${this.token}`;
    const observable = this.http.delete<ApiResponse<TransactionModel>>(
      endpoint,
      this.httpOptions
    );
    return this.handleApiResponse(observable);
  }

  getCurrentClient() {
    const endpoint = `${this.apiURL}/clients/current`;
    return this.sendRequest<Client>(this.http, endpoint);
  }

  getAddCardToken(): Observable<ApiResponse<{ btToken: string }>> {
    const endpoint = `${this.apiURL}/transaction/add-card-token`;
    return this.sendRequest<{ btToken: string }>(this.http, endpoint);
  }

  addPatronCardToDigitalWallet(data: {
    patronId: number;
    paymentNonce: string;
    useAsDefaultCard?: boolean;
  }): Observable<ApiResponse<PatronModel>> {
    const endpoint = `${this.apiURL}/transaction/add/digital-wallet`;
    return this.sendRequest<PatronModel>(this.http, endpoint, data);
  }

  checkoutTransaction(transactionId: string, payload: PaymentDetails) {
    let {
      accountCredit,
      digitalWallet,
      nonce,
      cardType,
      paymentMethod,
      firstName,
      lastName,
      email,
      address,
      phone,
    } = payload;
    const { credits, payFull } = accountCredit;
    const endpoint = `${this.apiURL}/transaction/${transactionId}/checkout?token=${this.token}`;
    if (nonce && digitalWallet) {
      nonce = null;
      cardType = digitalWallet.cardType.description;
    }
    const body = {
      payments: {
        cardType: cardType ? cardType.toLocaleLowerCase() : null,
        paymentNonce: nonce,
        paymentMethod,
        digitalWalletId: digitalWallet ? String(digitalWallet.id) : null,
        credit: credits,
      },
      customer: {
        firstName,
        lastName,
        email,
        address,
        ...(phone && { phone }),
      },
    };
    return this.sendRequest<TransactionModel>(this.http, endpoint, body);
  }

  postAssociatePatronToCart(
    patronOktaId: string,
    email: string,
    transactionId: number,
    token: string
  ) {
    let endpoint = `${this.apiURL}/transaction/${transactionId}/add/patron?token=${token}`;
    return this.sendRequest<PatronModel | null>(this.http, endpoint, {
      customer: patronOktaId,
      email,
    }).pipe(
      map(obj => {
        if (obj.data === null) {
          throw new ApiException({
            code: '',
            message: 'Patron Not Found',
            data: { isPatronNotFounError: true },
          });
        }

        return obj;
      }),
      catchError(error => {
        console.error('API ERROR: ', error);
        return throwError(() => new ApiException(error.error.error));
      })
    );
  }
}
